import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { COMPANY_INFO, NAV_LINKS } from '../data/content';

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cmsNavLinks, setCmsNavLinks] = useState(NAV_LINKS);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch menu items from CMS
  useEffect(() => {
    async function fetchCmsMenuItems() {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/content/menu_items');
        if (res.ok) {
          const items = await res.json();
          if (Array.isArray(items) && items.length > 0) {
            const sorted = items
              .filter((item) => item.active !== false)
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((item) => ({ label: item.title, path: item.path }));
            if (sorted.length > 0) {
              setCmsNavLinks(sorted);
            }
          }
        }
      } catch (err) {
        console.error('Kunne ikke hente menu_items fra CMS, bruger standard navigation:', err);
      }
    }
    fetchCmsMenuItems();
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50 w-full transition-all duration-200">
      {/* Top Banner - Danish Sole Distributor Notice */}
      <div className="bg-[#122a4e] text-slate-200 text-xs py-2 px-4 border-b border-[#1d3b66]">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-2 font-medium">
            <span className="inline-block w-2 h-2 rounded-full bg-[#446ba9] animate-pulse"></span>
            <span className="text-white font-semibold hidden sm:inline">{COMPANY_INFO.name}</span>
            <span className="text-slate-400 hidden sm:inline">•</span>
            <span className="text-slate-200 font-medium">{COMPANY_INFO.soleDistributorText}</span>
          </div>
          <div className="flex items-center gap-4 text-xs font-medium">
            <a
              href={COMPANY_INFO.phoneRaw}
              className="hover:text-white transition-colors flex items-center gap-1.5 whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-[#446ba9] text-[10px]" aria-hidden="true" />
              <span className="whitespace-nowrap">{COMPANY_INFO.phone}</span>
            </a>
            <span className="text-slate-500">|</span>
            <a
              href={COMPANY_INFO.emailRaw}
              className="hover:text-white transition-colors flex items-center gap-1.5 whitespace-nowrap"
            >
              <i className="fa-solid fa-envelope text-[#446ba9] text-[10px]" aria-hidden="true" />
              <span className="whitespace-nowrap">{COMPANY_INFO.email}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation Bar */}
      <div
        className={`bg-white/95 backdrop-blur-md transition-shadow duration-300 border-b border-slate-200 ${
          isScrolled ? 'shadow-md py-3' : 'py-4'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          {/* Logo Branding */}
          <NavLink to="/" className="flex items-center gap-3 group">
            <img
              src="https://assets.auxodev.dk/assets/6db9bc06-2d1f-414d-89eb-eaf682030584/3elogo.png"
              alt="SYSTEM 3E Logo"
              className="h-6 sm:h-8 w-auto object-contain transition-transform group-hover:scale-105"
            />
          </NavLink>

          {/* Desktop Navigation Links - ALWAYS shown on screens >= 1200px */}
          <nav className="hidden min-[1200px]:flex items-center gap-1 min-[1300px]:gap-2">
            {cmsNavLinks
              .filter((link) => link.path !== '/kontakt')
              .map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <NavLink
                    key={link.path}
                    to={link.path}
                    className={`px-3 py-2 rounded-md text-sm font-semibold transition-all relative ${
                      isActive
                        ? 'text-[#122a4e] bg-[#122a4e]/5 font-bold'
                        : 'text-slate-700 hover:text-[#122a4e] hover:bg-slate-100'
                    }`}
                  >
                    {link.label}
                    {isActive && (
                      <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-[#122a4e] rounded-full" />
                    )}
                  </NavLink>
                );
              })}
          </nav>

          {/* Header Action Button & Mobile Burger Toggle */}
          <div className="flex items-center gap-3">
            <NavLink
              to="/kontakt"
              className="hidden sm:inline-flex items-center gap-2 bg-[#122a4e] hover:bg-[#1d3b66] text-white font-bold px-4 py-2.5 rounded-lg text-sm transition-all shadow-sm hover:shadow active:scale-95"
            >
              <span>Tal med os</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </NavLink>

            {/* Burger Menu Button - Only visible on screens < 1200px */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="max-[1199px]:flex hidden items-center justify-center p-2.5 rounded-lg text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-[#122a4e]"
              aria-label="Åbn menu"
              aria-expanded={mobileMenuOpen}
            >
              <i
                className={`fa-solid ${mobileMenuOpen ? 'fa-xmark text-xl' : 'fa-bars text-xl'}`}
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu Dropdown (< 1200px) */}
      {mobileMenuOpen && (
        <div className="max-[1199px]:block hidden bg-white border-b border-slate-200 shadow-xl animate-in slide-in-from-top-2 duration-200 max-h-[calc(100vh-100px)] overflow-y-auto">
          <div className="max-w-7xl mx-auto px-4 py-4 space-y-1">
            {cmsNavLinks.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <NavLink
                  key={link.path}
                  to={link.path}
                  className={`block px-4 py-3 rounded-lg text-base font-semibold transition-colors ${
                    isActive
                      ? 'bg-[#122a4e]/5 text-[#122a4e] font-bold border-l-4 border-[#122a4e] pl-3'
                      : 'text-slate-800 hover:bg-slate-100 hover:text-slate-950'
                  }`}
                >
                  {link.label}
                </NavLink>
              );
            })}
            <div className="pt-4 mt-2 border-t border-slate-100 flex flex-col gap-3 px-2">
              <NavLink
                to="/kontakt"
                className="w-full text-center bg-[#122a4e] hover:bg-[#1d3b66] text-white font-bold px-4 py-3 rounded-lg text-sm transition-colors shadow"
              >
                Tal med os om dit projekt
              </NavLink>
              <div className="text-xs text-slate-500 space-y-1 text-center pt-2">
                <p className="font-semibold text-slate-800">{COMPANY_INFO.name}</p>
                <p>
                  Tlf:{' '}
                  <a
                    href={COMPANY_INFO.phoneRaw}
                    className="text-[#122a4e] font-bold whitespace-nowrap"
                  >
                    {COMPANY_INFO.phone}
                  </a>
                </p>
                <p>
                  E-mail:{' '}
                  <a
                    href={COMPANY_INFO.emailRaw}
                    className="text-[#122a4e] font-bold whitespace-nowrap"
                  >
                    {COMPANY_INFO.email}
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
